// src/index.js
import './console-mute';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';

// Service worker registration helper
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

/* ===== inlined client-logger (no external file) ===== */
const createClientLogger = (opts = {}) => {
  const {
    sendUrl = '',
    enabled = process.env.NODE_ENV === 'production',
    bufferSize = 200,
    persistToLocalStorage = false,
  } = opts;

  const buffer = [];

  function pushEntry(entry) {
    buffer.push(entry);
    if (buffer.length > bufferSize) buffer.shift();
    if (persistToLocalStorage) {
      try {
        const existing = JSON.parse(localStorage.getItem('__client_logs__') || '[]');
        existing.push(entry);
        localStorage.setItem('__client_logs__', JSON.stringify(existing.slice(-bufferSize)));
      } catch (e) { /* ignore */ }
    }
  }

  function sendLogBatch(payload) {
    try {
      const body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(sendUrl, blob);
        return;
      }
      fetch(sendUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  }

  const originalConsole = { ...console };

  if (enabled) {
    ['log', 'info', 'warn', 'error', 'debug', 'trace'].forEach((k) => {
      console[k] = function () { /* muted in production */ };
    });
  }

  function captureConsoleCall(level, args) {
    try {
      const entry = {
        ts: new Date().toISOString(),
        level,
        message: args.map((a) => {
          try {
            if (typeof a === 'string') return a;
            return JSON.stringify(a);
          } catch (e) { return String(a); }
        }).join(' '),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      pushEntry(entry);
      if (level === 'error') sendLogBatch({ type: 'client_error', entries: [entry] });
    } catch (e) { /* ignore */ }
  }

  ['error', 'warn', 'info', 'log', 'debug'].forEach((level) => {
    const orig = originalConsole[level] || (() => {});
    console[`__orig_${level}`] = orig.bind(originalConsole);
    console[level] = (...args) => {
      captureConsoleCall(level, args);
      if (!enabled) orig.apply(originalConsole, args);
    };
  });

  window.addEventListener('error', (evt) => {
    try {
      const entry = {
        ts: new Date().toISOString(),
        level: 'error',
        message: evt.message || 'Script error',
        filename: evt.filename,
        lineno: evt.lineno,
        colno: evt.colno,
        stack: (evt.error && evt.error.stack) ? String(evt.error.stack) : '',
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      pushEntry(entry);
      //sendLogBatch({ type: 'client_error', entries: [entry] });
    } catch (e) { /* ignore */ }
  });

  window.addEventListener('unhandledrejection', (evt) => {
    try {
      const reason = evt.reason;
      const entry = {
        ts: new Date().toISOString(),
        level: 'error',
        message: reason && reason.message ? reason.message : String(reason),
        stack: reason && reason.stack ? String(reason.stack) : '',
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      pushEntry(entry);
      //sendLogBatch({ type: 'client_unhandledrejection', entries: [entry] });
    } catch (e) { /* ignore */ }
  });

  function flushBuffer() {
    if (!buffer.length) return;
    //sendLogBatch({ type: 'client_logs', entries: buffer.slice() });
  }

  function downloadLogs() {
    const logs = persistToLocalStorage
      ? (JSON.parse(localStorage.getItem('__client_logs__') || '[]'))
      : buffer;
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `client-logs-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return { flushBuffer, downloadLogs, buffer, originalConsole };
};

export const clientLogger = createClientLogger({
  sendUrl: '', // empty disables sending
  enabled: false, // don't mute console in dev; also stops posting
  bufferSize: 300,
  persistToLocalStorage: false,
});


if (process.env.NODE_ENV !== 'production') {
  window.__clientLogger = clientLogger;
}
/* ===== end client-logger ===== */

const rootEl = document.getElementById('root');

if (!rootEl) {
  // graceful error if #root not present
  //console.error('React root element (#root) not found in DOM.');
} else {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </React.StrictMode>
  );
}

// Register service worker (attempts to register public/service-worker.js).
// We call this after render so SW lifecycle doesn't block initial paint.
try {
  serviceWorkerRegistration.register({
    onUpdate: (registration) => {
      console.log('[index] service worker update available', registration);
      // Dispatch a global event so App or other components can respond
      window.dispatchEvent(new CustomEvent('swUpdated', { detail: { registration } }));
    },
    onSuccess: (registration) => {
      console.log('[index] service worker installed', registration);
      window.dispatchEvent(new CustomEvent('swInstalled', { detail: { registration } }));
    },
  });
} catch (err) {
  console.warn('[index] serviceWorkerRegistration.register() failed:', err);
}

reportWebVitals();
